import { lazy, Suspense } from 'react';

import { useAppTranslation } from 'hooks/use-translates';

import { IconVariants } from 'core/generic/elements/icons';

import { PlayerSocial } from '@players/components/info-and-socials-section/info-and-socials-section';

import styles from './player-info.module.scss';

const Button = lazy(() => import('core/generic/elements/button'));

type PlayerInfoProps = {
	label: string;
	type?: 'text' | 'socials';
	value?: string | PlayerSocial[];
};

const SocialIconMap: Record<string, IconVariants> = {
	'Twitter/X': 'BrandX',
	Instagram: 'BrandInstagram',
	Twitch: 'BrandTwitch',
	TikTok: 'BrandTikTok',
};
const SocialLabelMap: Record<string, string> = {
	'Twitter/X': 'players_profile.social_twitter',
	Instagram: 'players_profile.social_instagram',
};

const getSocialLink = (type: string, username: string) => {
	switch (type) {
		case 'Twitter/X':
			return `https://x.com/${username}`;
		case 'Instagram':
			return `https://www.instagram.com/${username}`;
		case 'Twitch':
			return `https://www.twitch.tv/${username}`;
		case 'TikTok':
			return `https://www.tiktok.com/@${username}`;
		default:
			return '#';
	}
};

export const PlayerInfo = ({
	type = 'text',
	label,
	value,
}: PlayerInfoProps) => {
	const { t } = useAppTranslation();
	return (
		<Suspense fallback={null}>
			<div className={styles['player-info']}>
				<span className={`${styles['player-info__label']} body-small`}>
					{label}
				</span>
				{type === 'text' && (
					<span
						className={`${styles['player-info__value']} display-medium bold`}
					>
						{value as string}
					</span>
				)}
				{type === 'socials' && Array.isArray(value) && (
					<div className={styles['player-info__socials']}>
						{value
							.filter((social) => !social.hide)
							.map((social, index) => (
								<Button
									key={index}
									variant={'secondary'}
									dimension="large"
									label={
										SocialLabelMap[social.type]
											? t(
													SocialLabelMap[social.type],
													undefined,
													social.type
											  )
											: social.type
									}
									icon={SocialIconMap[social.type]}
									className={
										styles['player-info__social-button']
									}
									href={getSocialLink(
										social.type,
										social.username || ''
									)}
									target="_blank"
								/>
							))}
					</div>
				)}
			</div>
		</Suspense>
	);
};
